import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui-kit/dialog';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Textarea } from '@/components/ui-kit/textarea';
import { Brain } from 'lucide-react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuerySSE } from '@/modules/gpt-chats/hooks/use-agents';
import { ISSEQueryAgentPayload } from '@/modules/gpt-chats/types/agent.service.type';
import { parseSSEBuffer } from '@/modules/gpt-chats/shared/utils/parse-sse';
import { GREETINGS_GENERATION_PROMPT } from '@/modules/gpt-chats/constants';
import { AnimatedDots } from '@/modules/gpt-chats/shared/utils/chat-event-messages';

type GreetingAIModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onUse: (greeting: string) => void;
  agentName?: string;
  greetingValue: string;
};

export const GreetingAIModal = ({
  isOpen,
  onClose,
  onUse,
  agentName,
  greetingValue,
}: GreetingAIModalProps) => {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generatedGreeting, setGeneratedGreeting] = useState('');
  const [statusMessage, setStatusMessage] = useState(t('GENERATING'));
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { streamQuery } = useQuerySSE();

  const handleClose = useCallback(() => {
    onClose();
    setGeneratedGreeting('');
    setIsGenerating(false);
  }, [onClose]);

  const generateGreeting = useCallback(async () => {
    try {
      setIsGenerating(true);
      setGeneratedGreeting('');
      setStatusMessage(t('GENERATING'));

      const payload: ISSEQueryAgentPayload = {
        query:
          greetingValue ?? t('DEFAULT_GREETING', { name: agentName || t('ASSISTANT') }),
        base_prompt: GREETINGS_GENERATION_PROMPT(agentName || t('ASSISTANT')),
        last_n_turns: 5,
        enable_summary: false,
        enable_tools: false,
        enable_next_suggestion: false,
        call_from: 'blocks',
      };

      const stream = await streamQuery(payload);
      const reader = stream.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let isDone = false;

      while (!isDone) {
        const { done, value } = await reader.read();
        isDone = done;
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const { events, remaining } = parseSSEBuffer(buffer);
        buffer = remaining;

        events.forEach(({ eventType, eventData }) => {
          const content: string = (eventData.result || eventData.message) as string;
          switch (eventType) {
            case 'chat_response':
            case 'final_answer':
              if (content) {
                setGeneratedGreeting(content);
                setIsGenerating(false);
              }
              break;
            case 'error':
              setGeneratedGreeting(t('GENERATING_ERROR'));
              setIsGenerating(false);
              break;
          }
        });
      }
    } catch (error) {
      setGeneratedGreeting(t('FAILED_TO_GENERATE_GREETING'));
      setIsGenerating(false);
    }
  }, [streamQuery, greetingValue, agentName, t]);

  useEffect(() => {
    if (isOpen && generatedGreeting === '' && !isGenerating) {
      generateGreeting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleUse = () => {
    onUse(generatedGreeting);
    handleClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">{t('WELCOME_GUIDE')}</DialogTitle>
        </DialogHeader>

        <div className="pb-4">
          <label className="mb-3 block text-base font-medium">{t('GREETING')}</label>

          {isGenerating ? (
            <div className="flex min-h-[120px] items-center justify-center rounded-lg border bg-card">
              <div className="flex flex-col items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary">
                  <Brain className="h-6 w-6 animate-pulse text-white" />
                </div>
                <div className="flex flex-row items-center gap-2">
                  <span className="text-normal text-medium-emphasis">{statusMessage}</span>
                  <div className="mt-1 flex flex-col gap-3">
                    <AnimatedDots />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="relative">
              <Textarea
                ref={textareaRef}
                value={generatedGreeting}
                onChange={(e) => setGeneratedGreeting(e.target.value)}
                placeholder={t('GREETING_AI_PLACEHOLDER')}
                rows={4}
                className="resize-none border bg-card"
              />
              <div className="mt-2 text-right text-sm text-medium-emphasis">
                {t('CHARACTERS_COUNT', { count: generatedGreeting.length })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={generateGreeting}
            disabled={isGenerating}
            className="mr-auto pl-0 text-primary hover:bg-transparent hover:text-high-emphasis"
          >
            {t('REGENERATE')}
          </Button>
          <Button variant="outline" onClick={handleClose} className="px-8">
            {t('CANCEL')}
          </Button>
          <Button
            onClick={handleUse}
            disabled={isGenerating || !generatedGreeting}
            className="bg-primary px-8 text-white hover:bg-primary/90"
          >
            {t('USE')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
